import i18next from "i18next";
import React, { useEffect, useState } from "react";
import {
  FIELD_REQUIRED,
  INACTIVE,
  LOADING,
} from "../../../../i18n/keysTranslations";
import { Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetAppEvents } from "../../../../actions/adsCampaigns";
import { TIKTOK } from "../../../../utils/constants";
import { optionsAppEventsTikTok } from "../../../../utils/options";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";

const SelectAppEventTikTok = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  appID,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  onChangeAction = () => {},
  campaignType = "",
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const onGetAdvertisableApps = async () => {
    if (!appID) return;
    setLoading(true);
    const response = await dispatch(
      startGetAppEvents({
        integrationID,
        adAccountID,
        appID,
        platform: TIKTOK,
        campaignType,
      })
    );
    if (response.ok) {
      const data = response.data || [];
      const dataSet = new Set(data);
      const options = optionsAppEventsTikTok.map((eventOption) => ({
        ...eventOption,
        translated: true,
        disabled: !dataSet.has(eventOption.value),
      }));
      setOptions(options);
    }
    setLoading(false);
  };

  useEffect(() => {
    onGetAdvertisableApps();
    // eslint-disable-next-line
  }, [integrationID, adAccountID, appID]);

  const getFilteredOptions = () => {
    let initialOptions = options;

    return initialOptions;
  };

  const filteredOptions = getFilteredOptions();

  return (
    <Controller
      defaultValue={""}
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <>
          <Autocomplete
            {...field}
            id={name}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled}
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            loading={loading}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={(_, value) => {
              if (value?.value) {
                field.onChange(value.value);
                onChangeAction(value);
              } else {
                field.onChange(null);
                onChangeAction(null);
              }
            }}
            isOptionEqualToValue={(option, value) => option?.value === value}
            getOptionLabel={(option) => {
              const optionValue =
                typeof option === "object"
                  ? option
                  : filteredOptions.find((opt) => opt.value === option);
              if (optionValue) {
                const label = optionValue?.translated
                  ? t(optionValue?.label)
                  : optionValue?.label;
                return optionValue?.disabled
                  ? `${label} (${t(INACTIVE)})`
                  : label;
              }
              if (typeof option === "string") {
                return option;
              }
              return `${option}`;
            }}
            options={filteredOptions}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  component="li"
                  key={option.value}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="body2">
                    {option.translated ? t(option.label) : option.label}
                  </Typography>
                  {option.disabled && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      {t(INACTIVE)}
                    </Typography>
                  )}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                hiddenLabel={!label}
                sx={{
                  ...sxTextField,
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ".MuiFormHelperText-root": {
                    ml: "0 !important",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          {error?.message && (
            <Typography
              variant="caption"
              color={"error"}
              sx={{
                display: "block",
              }}
            >
              {error?.message}
            </Typography>
          )}
          {helperText && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
              }}
              color={"fields.placeholder"}
              dangerouslySetInnerHTML={{
                __html: helperText,
              }}
            />
          )}
        </>
      )}
    />
  );
};

export default SelectAppEventTikTok;
